import { LuiFileInputBox } from '@linzjs/lui';
import { LuiFileInputBoxProps } from '@linzjs/lui/dist/components/LuiFormElements/LuiFileInputBox/LuiFileInputBox';
import clsx from 'clsx';
import { ReactElement, useCallback, useMemo, useState } from 'react';

import { LuiModalAsync } from './LuiModalAsync';
import { LuiModalAsyncCallback, PromiseWithResolve } from './LuiModalAsyncContext';
import { LuiModalAsyncHeader } from './LuiModalAsyncHeader';
import { useShowAsyncModal } from './useShowAsyncModal';

export interface useLuiModalUploadProps extends Omit<LuiFileInputBoxProps, 'onValidFileSelected'> {
  title?: string;
  content?: ReactElement | string;
  validateFile?: (file: File) => string;
}

/**
 * A generic template for common types of modals.
 */
export const LuiModalUpload = ({
  validateFile,
  resolve,
  title,
  width,
  height,
  content,
  ...props
}: useLuiModalUploadProps & LuiModalAsyncCallback<File>): ReactElement => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const uploadFile = useCallback(
    (file: File) => {
      const error = validateFile?.(file);
      if (error) {
        setErrorMessage(error);
      } else {
        resolve(file);
      }
    },
    [resolve, validateFile],
  );

  return (
    <LuiModalAsync
      className={clsx('LuiModalPrefab', `LuiModalPrefab-file`)}
      closeOnOverlayClick={true}
      style={{ width: width ?? 512, minHeight: height, height: height }}
    >
      {title && <LuiModalAsyncHeader title={title ?? 'Upload file...'} closeButton />}
      {content}
      <LuiFileInputBox
        {...props}
        width={'100%'}
        showMustSelectFileError={!!errorMessage}
        customFileErrorMessage={errorMessage ?? props.customFileErrorMessage}
        onValidFileSelected={uploadFile}
      />
    </LuiModalAsync>
  );
};

export const useLuiModalUpload = () => {
  const { showModal, modalOwnerRef } = useShowAsyncModal();

  const showUploadModal = useCallback(
    (props: useLuiModalUploadProps): PromiseWithResolve<File> => {
      return showModal(LuiModalUpload, props) as PromiseWithResolve<File>;
    },
    [showModal],
  );

  return useMemo(
    () => ({
      modalOwnerRef,
      showUploadModal,
    }),
    [modalOwnerRef, showUploadModal],
  );
};
